import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { therapistApi } from '../api/therapistApi';
import type {
  TherapistDashboardStats,
  TherapistAppointment,
  TherapistProfile,
  TherapistEarnings,
  AvailabilitySlot,
  ClientSummary,
  TherapistNotification,
  ScheduleFilters,
} from '../types/therapist-dashboard.types';

/**
 * Hook for fetching therapist dashboard statistics
 */
export function useTherapistDashboardStats() {
  return useQuery<TherapistDashboardStats, Error>({
    queryKey: ['therapist-dashboard-stats'],
    queryFn: () => therapistApi.getDashboardStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for fetching therapist appointments
 */
export function useTherapistAppointments(filters?: ScheduleFilters) {
  return useQuery<TherapistAppointment[], Error>({
    queryKey: ['therapist-appointments', filters],
    queryFn: () => therapistApi.getAppointments(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for fetching therapist profile
 */
export function useTherapistProfile() {
  return useQuery<TherapistProfile, Error>({
    queryKey: ['therapist-profile'],
    queryFn: () => therapistApi.getProfile(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook for updating therapist profile
 */
export function useUpdateTherapistProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<TherapistProfile>) => therapistApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-profile'] });
    },
  });
}

/**
 * Hook for fetching therapist earnings
 */
export function useTherapistEarnings() {
  return useQuery<TherapistEarnings, Error>({
    queryKey: ['therapist-earnings'],
    queryFn: () => therapistApi.getEarnings(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for fetching therapist availability
 */
export function useTherapistAvailability() {
  return useQuery<AvailabilitySlot[], Error>({
    queryKey: ['therapist-availability'],
    queryFn: () => therapistApi.getAvailability(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for updating therapist availability
 */
export function useUpdateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slots: AvailabilitySlot[]) => therapistApi.updateAvailability(slots),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-availability'] });
    },
  });
}

/**
 * Hook for fetching therapist's clients
 */
export function useTherapistClients() {
  return useQuery<ClientSummary[], Error>({
    queryKey: ['therapist-clients'],
    queryFn: () => therapistApi.getClients(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for updating client notes
 */
export function useUpdateClientNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, notes }: { clientId: string; notes: string }) =>
      therapistApi.updateClientNotes(clientId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-clients'] });
      queryClient.invalidateQueries({ queryKey: ['therapist-appointments'] });
    },
  });
}

/**
 * Hook for fetching therapist notifications
 */
export function useTherapistNotifications() {
  return useQuery<TherapistNotification[], Error>({
    queryKey: ['therapist-notifications'],
    queryFn: () => therapistApi.getNotifications(),
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for marking notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => therapistApi.markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-notifications'] });
    },
  });
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => therapistApi.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-notifications'] });
    },
  });
}

/**
 * Hook for updating appointment status
 */
export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      status,
    }: {
      appointmentId: string;
      status: 'completed' | 'no_show' | 'cancelled';
    }) => therapistApi.updateAppointmentStatus(appointmentId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-appointments'] });
      queryClient.invalidateQueries({ queryKey: ['therapist-dashboard-stats'] });
    },
  });
}

/**
 * Hook for fetching a single therapist appointment by ID
 * Fetches all appointments and filters by ID (uses existing endpoint)
 */
export function useTherapistAppointmentById(id: string) {
  const queryResult = useQuery<TherapistAppointment[], Error>({
    queryKey: ['therapist-appointments'],
    queryFn: () => therapistApi.getAppointments(),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: !!id,
  });

  // Find the specific appointment from the list
  const appointment = queryResult.data?.find((apt) => apt.id === id);

  return {
    ...queryResult,
    data: appointment,
    isNotFound: queryResult.isSuccess && !appointment,
  };
}
